import { graphql } from "gatsby"
import React, { useContext } from "react"
import Col from "react-bootstrap/Col"
import Row from "react-bootstrap/Row"
import { useTranslation } from "react-i18next"

import CoreHeading from "@atoms/CoreHeading/CoreHeading"
import Subtitle from "@atoms/Subtitle/Subtitle"
import Blocks from "@blocks/Blocks/Blocks"
import Breadcrumb from "@blocks/Breadcrumb/Breadcrumb"
import Classic from "@blocks/Classic/Classic"
import Layout from "@components/layout.js"
import SEO from "@components/seo"
import { SiteContext } from "@context/Provider"
import i18nWrapper from "@lang/i18nWrapper"
import Card from "@molecules/Card/Card"
import Combo from "@molecules/Combo/Combo"
import HeroCombo from "@molecules/HeroCombo/HeroCombo"
import InfoBoxAlt from "@molecules/InfoBoxAlt/InfoBoxAlt"
import Carousel from "@organisms/Carousel/Carousel"
import HeroHighlightMinor from "@organisms/HeroHighlightMinor/HeroHighlightMinor"

const Page = (props) => {
  const { t } = useTranslation()
  const {
    data: { single: page, relatedTaggedContent, settings },
  } = props

  const {
    title,
    seo,
    uri,
    date,
    blocks,
    content,
    language,
    translations,
    featuredImage,
    postMeta,
    postMetaRelated,
    categories,
    jwPlayerIDs,
    customTags,
  } = page

  const currentLanguage = language ? language.slug : "fi"

  // Context
  const context = useContext(SiteContext)
  context.relatedTaggedContent = relatedTaggedContent.edges
  context.currentLanguage = currentLanguage
  context.title = title
  context.jwPlayerIDs = jwPlayerIDs

  if (categories && categories.nodes.length > 0) {
    context.ancestors = {
      nodes: [
        {
          title: categories.nodes[0].name,
          uri: categories.nodes[0].uri,
        },
      ],
    }
  }

  const categoryName =
    categories && categories.nodes.length > 0 ? categories.nodes[0].name : null

  const { juridiikanNewsletter, juristiNewsletter } =
    settings?.siteSettings?.siteSettings || {}

  const tagNames =
    customTags?.nodes?.length > 0
      ? customTags.nodes.map((node) => {
          return node.name
        })
      : []

  const newsletterSource = tagNames.includes("Juristikirje")
    ? juristiNewsletter
    : juridiikanNewsletter

  // Related content

  const relatedItems =
    relatedTaggedContent.edges.length > 0
      ? relatedTaggedContent.edges.map((card, index) => {
          const { id, title, uri, featuredImage, categories } = card.node

          const tagline =
            categories?.nodes.length > 0 ? categories.nodes[0]?.name : null

          return (
            <Col key={id} md="4">
              <Card
                headingSize={"h3"}
                image={featuredImage ? featuredImage.node : false}
                imageAspectRatio={"16:9"}
                tagline={tagline}
                title={title}
                url={uri}
                internalLink={true}
              />
            </Col>
          )
        })
      : null

  // Misc

  const aspectRatio = "7:5"

  // Preload the first image

  let preLoadImage = null
  let preLoadImageWidths = null

  if (featuredImage) {
    preLoadImage = featuredImage.node
    preLoadImageWidths = [480, 720, 1440, 1600]
  }

  const postAuthor = postMeta.postAuthor ? postMeta.postAuthor : null

  const postMetaRelatedCardsHeading = postMetaRelated?.cardsHeading

  const postMetaRelatedCards = postMetaRelated?.cards?.map((slide, index) => {
    return {
      title: slide?.link?.title,
      url: slide?.link?.url,
      ...slide,
    }
  })

  const postMetaRelatedCombos = postMetaRelated?.combos?.map((slide, index) => {
    const { heading, text, image, links } = slide
    return (
      <section className="global-combo" key={index}>
        <Combo
          variant={index % 2 ? "highlight" : "regular"}
          title={heading}
          text={text}
          image={image}
          aspectRatio="7:5"
          links={links}
        />
      </section>
    )
  })

  // Tags for GTM
  // https://mixedanalytics.com/blog/track-popular-blog-tags-categories-google-analytics/

  const tagSlugs =
    customTags?.nodes?.length > 0
      ? customTags.nodes
          .map((node) => {
            return node.slug
          })
          .join(" ")
      : ""

  return (
    <Layout
      translations={translations}
      currentLanguage={currentLanguage}
      lang={language ? language.locale : "FI"}
    >
      <SEO
        title={seo?.title}
        uri={uri}
        lang={language ? language.locale : "FI"}
        headJson={seo?.headJSON}
        translations={translations}
        preLoadImage={preLoadImage}
        preLoadImageWidths={preLoadImageWidths}
        preLoadImageAspectRatio={aspectRatio}
      />
      {featuredImage && (
        <HeroCombo
          title={title}
          image={featuredImage.node}
          aspectRatio={aspectRatio}
          caption={featuredImage.node?.caption}
        />
      )}
      {!featuredImage && <CoreHeading text={title} level="h1" />}
      <Breadcrumb />
      <div className="max-vvw">
        <Subtitle text={date} />
      </div>
      <div className="article-tags" data-tags={tagSlugs}></div>

      {blocks && <Blocks blocks={blocks} />}
      {!blocks && <Classic content={content} />}

      {postAuthor && postAuthor.personMeta && (
        <InfoBoxAlt
          heading={`${t("Author")} ${postAuthor.personMeta.name}`}
          list={postAuthor.personMeta.bulletlist?.split("\r\n")}
          link={{
            title: postAuthor.personMeta.archivelinktext,
            url: postAuthor.uri,
          }}
        />
      )}

      {categoryName === "Juridiikan ja talouden uutiset" && (
        <HeroHighlightMinor
          title={newsletterSource.heading}
          image={newsletterSource.image}
          lead={newsletterSource.text}
          links={[{ link: newsletterSource.link }]}
        />
      )}

      {postMetaRelatedCards && (
        <section className="base-margin-top base-margin-bottom text-align-left">
          {postMetaRelatedCardsHeading && (
            <CoreHeading
              text={postMetaRelatedCardsHeading}
              level="h2"
              align="wide"
            />
          )}
          <Carousel slides={postMetaRelatedCards} />
        </section>
      )}
      {postMetaRelatedCombos && (
        <section className="base-margin-top-xxl base-margin-bottom-xxl text-align-left">
          {postMetaRelatedCombos}
        </section>
      )}
      {relatedItems && (
        <section className="base-margin-top-xl base-margin-bottom-large text-align-left">
          <div className="base-margin-bottom-small">
            <h2>{t("Related content")}</h2>
          </div>
          <Row className="mobile-bottom-margins">{relatedItems}</Row>
        </section>
      )}
    </Layout>
  )
}

export default i18nWrapper("Page")(Page)

export const query = graphql`
  query SINGLE_POST($id: String!, $customTagsArray: [String]) {
    settings: wp {
      siteSettings {
        siteSettings {
          juridiikanNewsletter {
            heading
            text
            image {
              mediaDetails {
                height
                width
              }
              cloudinaryDetails {
                public_id
                original_filename
                format
              }
              sourceUrl
              altText
              altTextOriginal
            }
            link {
              url
              target
              title
            }
          }
          juristiNewsletter {
            heading
            text
            image {
              mediaDetails {
                height
                width
              }
              cloudinaryDetails {
                public_id
                original_filename
                format
              }
              sourceUrl
              altText
              altTextOriginal
            }
            link {
              url
              target
              title
            }
          }
        }
      }
    }
    relatedTaggedContent: allWpPost(
      filter: {
        customTags: { nodes: { elemMatch: { id: { in: $customTagsArray } } } }
        id: { ne: $id }
      }
      sort: { date: DESC }
      limit: 3
    ) {
      edges {
        node {
          id
          title
          uri
          categories {
            nodes {
              name
            }
          }
          featuredImage {
            node {
              mediaDetails {
                width
                height
              }
              cloudinaryDetails {
                public_id
                original_filename
                format
              }
              sourceUrl
              altText
              altTextOriginal
            }
          }
        }
      }
    }
    single: wpPost(id: { eq: $id }) {
      title
      seo {
        title
        headJSON
      }
      uri
      content
      date(formatString: "DD.MM.Y HH:mm")
      language {
        slug
        locale
      }
      categories {
        nodes {
          name
          uri
        }
      }
      featuredImage {
        node {
          mediaDetails {
            width
            height
          }
          cloudinaryDetails {
            public_id
            original_filename
            format
          }
          sourceUrl
          altText
          altTextOriginal
          caption
        }
      }
      postMeta {
        postAuthor {
          ... on WpPerson {
            id
            uri
            personMeta {
              name
              bulletlist
              archivelinktext
            }
          }
        }
      }
      postMetaRelated {
        cardsHeading
        cards {
          image {
            mediaDetails {
              width
              height
            }
            cloudinaryDetails {
              public_id
              original_filename
              format
            }
            sourceUrl
          }
          link {
            url
            title
          }
        }
        combos {
          heading
          text
          image {
            mediaDetails {
              width
              height
            }
            cloudinaryDetails {
              public_id
              original_filename
              format
            }
            sourceUrl
          }
          links {
            link {
              url
              title
              target
            }
          }
        }
      }
      translations {
        title
        uri
        language {
          slug
          locale
        }
      }
      jwPlayerIDs
      customTags {
        nodes {
          slug
          name
        }
      }
      #############
      blocks {
        ...BlocksFirstLevel
        ...Blocks
      }
    }
  }
`
